import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { BadgeArt } from "@/components/BadgeArt";
import { API_URL } from "@/lib/api";

const DIFFICULTIES = ["4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "college"];
const TOGGLE_META = [
  ["intermission_code", "Intermission check-in code"],
  ["kick_absent", "Kick players who skip check-in"],
  ["takeover_video", "Mascot takeover video"],
  ["phone_sounds", "Phone sounds"],
  ["ads_on_tv", "Ads on TV"],
  ["home_play", "Play-at-home room"],
];

function AdminSlider({ label, value, min, max, step, unit, onChange, testId }) {
  return (
    <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4 text-left">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-cream">{label}</span>
        <span className="font-mono text-sm font-bold text-amber-soft" data-testid={`${testId}-value`}>{value} {unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-3 w-full accent-amber" data-testid={testId} />
    </div>
  );
}

export function ToggleRow({ label, on, onToggle, testId, hint }) {
  return (
    <button type="button" data-testid={testId} onClick={onToggle}
      className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all ${on ? "border-mint/50 bg-mint/10" : "border-ice/15 bg-navy-900/60"}`}>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-cream">{label}</span>
        {hint && <span className="mt-0.5 block text-[11px] leading-snug text-mut">{hint}</span>}
      </span>
      <span className={`ml-3 flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${on ? "bg-mint" : "bg-navy-700"}`}>
        <span className={`h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
      </span>
    </button>
  );
}

function SubscriptionsTable({ authHeader }) {
  const [data, setData] = useState(null);
  const load = () => {
    fetch(`${API_URL}/admin/subscriptions`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);
  if (!data) return <p className="py-4 text-sm text-mut">Loading subscriptions…</p>;
  const statusPill = (row) => {
    if (row.type === "demo") return <span className="rounded-full bg-grape/20 px-3 py-1 font-mono text-[10px] font-bold uppercase text-[#dcb6ff]">demo</span>;
    if (row.big_screen_entitled) return <span className="rounded-full bg-mint/20 px-3 py-1 font-mono text-[10px] font-bold uppercase text-mint" data-testid={`admin-subs-access-${row.code}`}>access on</span>;
    return <span className="rounded-full bg-coral/15 px-3 py-1 font-mono text-[10px] font-bold uppercase text-coral" data-testid={`admin-subs-access-${row.code}`}>access off</span>;
  };
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-ice/15" data-testid="admin-subscriptions-table">
        <table className="w-full text-left text-sm">
          <thead className="bg-navy-900/80 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">
            <tr><th className="px-4 py-3">Room</th><th className="px-4 py-3">Kind</th><th className="px-4 py-3">Subscription</th><th className="px-4 py-3">Paid through</th><th className="px-4 py-3">Big Screen</th></tr>
          </thead>
          <tbody>
            {data.venues.map((v) => (
              <tr key={v.id} className="border-t border-ice/10" data-testid="admin-subs-row">
                <td className="px-4 py-3 font-bold text-cream">{v.name}<span className="ml-2 font-mono text-[10px] text-mut">{v.code}</span></td>
                <td className="px-4 py-3 font-mono text-[11px] uppercase text-mut">{v.type}</td>
                <td className="px-4 py-3">
                  <span className={`font-mono text-[11px] font-bold uppercase ${v.subscription_status === "active" ? "text-mint" : v.subscription_status === "past_due" ? "text-amber-soft" : "text-mut"}`} data-testid={`admin-subs-status-${v.code}`}>{v.subscription_status}</span>
                  {v.cancel_at_period_end && <span className="ml-2 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase text-coral">ends at period end</span>}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-mut">{v.current_period_end ? new Date(v.current_period_end).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">{statusPill(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.transactions.length > 0 && (
        <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4">
          <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-mut">Recent payments</div>
          <div className="space-y-1.5">
            {data.transactions.slice(0, 8).map((t) => (
              <div key={t.id} className="flex items-center justify-between font-mono text-[11px] text-mut" data-testid="admin-subs-transaction">
                <span>{t.venue_code || "—"} · {t.lookup_key}</span>
                <span className={t.payment_status === "paid" ? "text-mint" : "text-amber-soft"}>${(t.amount / 100).toFixed(2)} · {t.payment_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdStatsPanel({ authHeader }) {
  const [data, setData] = useState(null);
  const load = () => {
    fetch(`${API_URL}/admin/ad-stats`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="py-3 text-sm text-mut">Loading ad performance…</p>;
  if (!data.ads.length) return <p className="py-3 text-sm text-mut" data-testid="admin-ads-empty">No venue ads yet — ads appear here the moment a bar uploads one.</p>;
  return (
    <div className="overflow-hidden rounded-2xl border border-ice/15" data-testid="admin-ad-stats">
      <table className="w-full text-left text-sm">
        <thead className="bg-navy-900/80 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">
          <tr><th className="px-4 py-3">Venue</th><th className="px-4 py-3">Link</th><th className="px-4 py-3">Views</th><th className="px-4 py-3">Clicks</th><th className="px-4 py-3">CTR</th></tr>
        </thead>
        <tbody>
          {data.ads.map((a) => (
            <tr key={a.venue_code} className="border-t border-ice/10" data-testid="admin-ads-row">
              <td className="px-4 py-3 font-bold text-cream">{a.venue_name}<span className="ml-2 font-mono text-[10px] text-mut">{a.venue_code}</span>{!a.active && <span className="ml-2 rounded-full bg-navy-700 px-2 py-0.5 font-mono text-[9px] uppercase text-mut">paused</span>}</td>
              <td className="px-4 py-3 font-mono text-[11px]">{a.has_link ? <span className="text-mint">website linked</span> : <span className="text-mut">no link</span>}</td>
              <td className="px-4 py-3 font-mono-num font-bold text-cream" data-testid={`admin-ad-views-${a.venue_code}`}>{a.impressions}</td>
              <td className="px-4 py-3 font-mono-num font-bold text-mint" data-testid={`admin-ad-clicks-${a.venue_code}`}>{a.clicks}</td>
              <td className="px-4 py-3 font-mono text-[11px] text-mut">{a.impressions ? `${Math.round((a.clicks / a.impressions) * 100)}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformTab({ plat, setPlat, save, busy, authHeader }) {
  const set = (section, key, value) => setPlat({ ...plat, [section]: { ...plat[section], [key]: value } });
  const stripeOn = plat.stripe_mode === "on";
  return (
    <>
      <div className={`rounded-2xl border-2 px-6 py-5 transition-all ${stripeOn ? "border-mint/50 bg-mint/5" : "border-amber/50 bg-amber/5"}`} data-testid="admin-stripe-mode-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg font-extrabold uppercase tracking-wide text-cream">Stripe Payment Mode</div>
            <p className="mt-1 text-[12px] leading-snug text-mut">
              {stripeOn
                ? "ON — paid mode. Venues and home players need an active subscription to launch Big Screen Mode. Players stay free."
                : "OFF — free/testing mode. Everything is free platform-wide; no checkout or subscription is ever required."}
            </p>
          </div>
          <button type="button" data-testid="admin-stripe-mode" aria-pressed={stripeOn}
            onClick={() => setPlat({ ...plat, stripe_mode: stripeOn ? "off" : "on" })}
            className={`flex h-9 w-16 shrink-0 items-center rounded-full p-1 transition-colors ${stripeOn ? "bg-mint" : "bg-navy-700"}`}>
            <span className={`h-7 w-7 rounded-full bg-white transition-transform ${stripeOn ? "translate-x-7" : ""}`} />
          </button>
        </div>
        <div className="mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-soft" data-testid="admin-stripe-mode-state">Currently: {stripeOn ? "ON — subscriptions required" : "OFF — platform is free"}</div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <ToggleRow testId="admin-maintenance-toggle" label="Maintenance mode" hint="Pauses every live game and shows a maintenance screen platform-wide" on={!!plat.maintenance_mode} onToggle={() => setPlat({ ...plat, maintenance_mode: !plat.maintenance_mode })} />
        <ToggleRow testId="admin-announcement-toggle" label="Global announcement" hint="A banner across every TV and phone" on={!!(plat.announcement && plat.announcement.enabled)} onToggle={() => set("announcement", "enabled", !(plat.announcement && plat.announcement.enabled))} />
      </div>
      <textarea data-testid="admin-announcement-message" value={(plat.announcement && plat.announcement.message) || ""} maxLength={300}
        onChange={(e) => set("announcement", "message", e.target.value)} rows={2}
        placeholder="Announcement text shown on every screen…"
        className="mt-3 w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-sm text-cream outline-none focus:border-amber" />

      <div className="mt-6 mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Platform features</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleRow testId="admin-feature-themed-games" label="Themed games" hint="Weekly special categories override General Knowledge" on={!!plat.features.themed_games} onToggle={() => set("features", "themed_games", !plat.features.themed_games)} />
        <ToggleRow testId="admin-feature-picture-questions" label="Picture questions" hint="Allow picture-based questions in games" on={!!plat.features.picture_questions} onToggle={() => set("features", "picture_questions", !plat.features.picture_questions)} />
        <ToggleRow testId="admin-feature-public-profiles" label="Public player profiles" hint="Players can appear on the public Champions Wall" on={!!plat.features.public_profiles} onToggle={() => set("features", "public_profiles", !plat.features.public_profiles)} />
        <ToggleRow testId="admin-feature-avatars" label="Avatars" hint="Players pick a character" on={!!plat.features.avatars} onToggle={() => set("features", "avatars", !plat.features.avatars)} />
        <ToggleRow testId="admin-feature-badges" label="Badges & achievements" on={!!plat.features.badges} onToggle={() => set("features", "badges", !plat.features.badges)} />
      </div>

      <div className="mt-6 mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Leaderboards</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleRow testId="admin-lb-national" label="National leaderboard" on={!!plat.leaderboards.national} onToggle={() => set("leaderboards", "national", !plat.leaderboards.national)} />
        <ToggleRow testId="admin-lb-regional" label="Regional leaderboards" on={!!plat.leaderboards.regional} onToggle={() => set("leaderboards", "regional", !plat.leaderboards.regional)} />
        <ToggleRow testId="admin-lb-venue" label="Venue leaderboards" on={!!plat.leaderboards.venue} onToggle={() => set("leaderboards", "venue", !plat.leaderboards.venue)} />
        <AdminSlider label="Nightly reset hour (platform time)" value={plat.leaderboards.nightly_reset_hour} min={0} max={23} step={1} unit=":00" onChange={(v) => set("leaderboards", "nightly_reset_hour", v)} testId="admin-lb-reset-hour" />
      </div>

      <div className="mt-6 mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Advertising</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleRow testId="admin-ads-enabled" label="Advertising" hint="Master switch for every ad surface" on={!!plat.advertising.enabled} onToggle={() => set("advertising", "enabled", !plat.advertising.enabled)} />
        <ToggleRow testId="admin-ads-home" label="Home advertising" on={!!plat.advertising.home_ads} onToggle={() => set("advertising", "home_ads", !plat.advertising.home_ads)} />
        <ToggleRow testId="admin-ads-venue-tv" label="Venue TV advertising" on={!!plat.advertising.venue_tv_ads} onToggle={() => set("advertising", "venue_tv_ads", !plat.advertising.venue_tv_ads)} />
        <ToggleRow testId="admin-ads-phone" label="Phone advertising" on={!!plat.advertising.phone_ads} onToggle={() => set("advertising", "phone_ads", !plat.advertising.phone_ads)} />
        <AdminSlider label="Ad rotation" value={plat.advertising.rotation_seconds} min={4} max={120} step={1} unit="sec" onChange={(v) => set("advertising", "rotation_seconds", v)} testId="admin-ads-rotation" />
        <AdminSlider label="Local ad radius" value={plat.advertising.radius_miles} min={5} max={200} step={5} unit="mi" onChange={(v) => set("advertising", "radius_miles", v)} testId="admin-ads-radius" />
      </div>

      <div className="mt-6 mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Streak fireworks</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleRow testId="admin-fireworks-enabled" label="Fireworks on big screen" hint="Bursts when a player hits the streak" on={!!plat.fireworks.enabled} onToggle={() => set("fireworks", "enabled", !plat.fireworks.enabled)} />
        <AdminSlider label="Streak length" value={plat.fireworks.streak_length} min={2} max={15} step={1} unit="in a row" onChange={(v) => set("fireworks", "streak_length", v)} testId="admin-fireworks-streak" />
        <AdminSlider label="Animation duration" value={plat.fireworks.duration_seconds} min={3} max={20} step={1} unit="sec" onChange={(v) => set("fireworks", "duration_seconds", v)} testId="admin-fireworks-duration" />
      </div>

      <div className="mt-6 mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Scoring</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <AdminSlider label="Streak bonus" value={plat.scoring.streak_bonus_pct} min={0} max={50} step={5} unit="% per streak" onChange={(v) => set("scoring", "streak_bonus_pct", v)} testId="admin-scoring-streak-bonus" />
        <AdminSlider label="Streak cap" value={plat.scoring.streak_cap_pct} min={0} max={200} step={10} unit="% max" onChange={(v) => set("scoring", "streak_cap_pct", v)} testId="admin-scoring-streak-cap" />
        <AdminSlider label="Fastest answer points" value={plat.scoring.speed_max} min={100} max={5000} step={100} unit="pts" onChange={(v) => set("scoring", "speed_max", v)} testId="admin-scoring-speed-max" />
        <AdminSlider label="Slowest answer points" value={plat.scoring.speed_min} min={0} max={1000} step={50} unit="pts" onChange={(v) => set("scoring", "speed_min", v)} testId="admin-scoring-speed-min" />
      </div>

      <button data-testid="admin-platform-save" onClick={save} disabled={!!busy}
        className="neon-btn mt-6 w-full rounded-full py-3.5 font-display font-extrabold uppercase tracking-wide disabled:opacity-60">
        Save platform settings
      </button>

      <div className="mt-8 border-t border-ice/10 pt-5">
        <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Subscriptions & access</div>
        <SubscriptionsTable authHeader={authHeader} />
      </div>

      <div className="mt-8 border-t border-ice/10 pt-5">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Ad performance</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut">advertising {plat.advertising.enabled ? "ON" : "OFF"}</span>
        </div>
        <AdStatsPanel authHeader={authHeader} />
      </div>

      <div className="mt-8 border-t border-ice/10 pt-5">
        <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Nightly reset & champions</div>
        <p className="mb-3 text-[11px] leading-snug text-mut">
          Every night at {plat.leaderboards.nightly_reset_hour}:00 {plat.leaderboards.timezone} each venue's board archives and crowns its nightly champion, and "tonight" starts fresh. Weekly metro and national boards keep their 7-day memory.
        </p>
        <NightlyArchivesPanel authHeader={authHeader} />
      </div>

      <div className="mt-8 border-t border-ice/10 pt-5">
        <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Regions &amp; metros</div>
        <p className="mb-3 text-[11px] leading-snug text-mut">
          Decide which league every room plays in — the metro bar board, the phone's place-in-metro card and nearby home ads all follow this.
        </p>
        <RegionsPanel authHeader={authHeader} />
      </div>
    </>
  );
}

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function RegionsPanel({ authHeader }) {
  const [rows, setRows] = useState(null);
  const [metros, setMetros] = useState([]);
  const [note, setNote] = useState("");
  const load = () => {
    fetch(`${API_URL}/admin/subscriptions`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setRows((await r.json()).venues); })
      .catch(() => {});
    fetch(`${API_URL}/admin/metros`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setMetros((await r.json()).metros); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = (code, patch) => setRows((rs) => rs.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  async function save(row) {
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/venues/${row.code}/region`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ region: row.region, zip_code: row.zip_code || "", metro_override: row.metro_override || "" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "failed");
      edit(row.code, { metro: d.metro });
      setNote(`${row.name} now plays in ${d.metro || "no league yet"}.`);
    } catch (ex) {
      setNote(ex.message === "failed" ? "Could not save — try again" : ex.message);
    }
  }
  if (!rows) return <p className="py-3 text-sm text-mut">Loading the room map…</p>;
  const leagues = {};
  rows.forEach((r) => { if (r.metro) leagues[r.metro] = (leagues[r.metro] || 0) + 1; });
  return (
    <div data-testid="admin-regions-panel">
      <p className="mb-3 text-[11px] leading-snug text-mut">
        Every room plays in one league: the admin override wins, then the typed city/region, then the venue's ZIP (major metros), then a statewide group. Leagues power the metro bar board and the phone's "your place in the metro" card.
      </p>
      {Object.keys(leagues).length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5" data-testid="admin-regions-summary">
          {Object.entries(leagues).sort((a, b) => b[1] - a[1]).map(([m, n]) => (
            <span key={m} className="rounded-full border border-ice/15 bg-navy-900/70 px-3 py-1 font-mono text-[10px] font-bold uppercase text-mut">{m} · {n}</span>
          ))}
        </div>
      )}
      {note && <p className="mb-2 text-[11px] font-bold text-mint" data-testid="admin-regions-note">{note}</p>}
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.code} className="rounded-2xl border border-ice/10 bg-navy-900/60 px-4 py-3" data-testid="admin-region-row">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-40 min-w-0 truncate text-sm font-bold text-cream">{row.name}<span className="ml-1.5 font-mono text-[10px] font-normal text-mut">{row.code}</span></span>
              <input data-testid={`admin-region-zip-${row.code}`} value={row.zip_code || ""} maxLength={5}
                onChange={(e) => edit(row.code, { zip_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })} placeholder="ZIP"
                className="w-20 shrink-0 rounded-xl border border-ice/20 bg-navy-900/70 px-2.5 py-2 font-mono text-sm text-cream outline-none focus:border-magenta" />
              <input data-testid={`admin-region-input-${row.code}`} value={row.region} maxLength={120}
                onChange={(e) => edit(row.code, { region: e.target.value })} placeholder="City / region (e.g. Gardner, KS)"
                className="min-w-0 flex-1 rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none focus:border-magenta" />
              <select data-testid={`admin-region-select-${row.code}`} value={row.metro_override || ""}
                onChange={(e) => edit(row.code, { metro_override: e.target.value })}
                className="w-44 rounded-xl border border-ice/20 bg-navy-900/70 px-2 py-2 text-sm text-cream outline-none focus:border-magenta">
                <option value="">Auto (region / ZIP)</option>
                {metros.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button data-testid={`admin-region-save-${row.code}`} onClick={() => save(row)}
                className="rounded-full bg-magenta/20 px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase text-[#ff9df2] transition-colors hover:bg-magenta/30">Save</button>
            </div>
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mut">
              plays in <span className="font-bold text-amber-soft" data-testid={`admin-region-league-${row.code}`}>{row.metro || "no league yet"}</span>{row.metro_override ? " · override" : " · automatic"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScheduleTab({ cats, authHeader }) {
  const [sched, setSched] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch(`${API_URL}/schedule`).then(async (r) => { if (r.ok) setSched(await r.json()); }).catch(() => {});
  }, []);
  const setDay = (d, patch) => setSched({ ...sched, days: { ...sched.days, [d]: { ...sched.days[d], ...patch } } });
  async function save() {
    setBusy(true); setNote(""); setErr("");
    try {
      const r = await fetch(`${API_URL}/admin/schedule`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ days: sched.days }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "failed");
      setSched(d.schedule);
      setNote("Schedule saved — themed windows apply from the next game");
    } catch (ex) {
      setErr(ex.message === "failed" ? "Could not save — try again" : ex.message);
    } finally {
      setBusy(false);
    }
  }
  if (!sched) return <p className="py-4 text-sm text-mut">Loading the weekly schedule…</p>;
  return (
    <>
      <p className="mb-1 text-sm text-mut">Seven themed nights. Reminders only — the host always starts the game; anything started inside a themed window plays that day's theme.</p>
      <p className="mb-4 text-[11px] text-mut">If a theme's bank runs thin, General Knowledge takes over so the night never dies. Times are {sched.timezone}. The nightly leaderboard reset runs at {sched.reset_hour}:00 {sched.timezone}.</p>
      <div className="space-y-2">
        {DAY_ORDER.map((d) => {
          const row = sched.days[d];
          return (
            <div key={d} className={`rounded-2xl border px-4 py-3 transition-all ${row.enabled ? "border-magenta/50 bg-magenta/5" : "border-ice/15 bg-navy-900/60"}`} data-testid={`admin-sched-${d}`}>
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" data-testid={`admin-sched-toggle-${d}`} aria-pressed={row.enabled} onClick={() => setDay(d, { enabled: !row.enabled })}
                  className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors ${row.enabled ? "bg-mint" : "bg-navy-700"}`}>
                  <span className={`h-5 w-5 rounded-full bg-white transition-transform ${row.enabled ? "translate-x-5" : ""}`} />
                </button>
                <span className="w-20 font-display text-sm font-extrabold uppercase tracking-wide text-cream">{d.slice(0, 3)}</span>
                <select data-testid={`admin-sched-cat-${d}`} value={row.category_id} disabled={!row.enabled}
                  onChange={(e) => setDay(d, { category_id: e.target.value })}
                  className="min-w-0 flex-1 rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none focus:border-magenta disabled:opacity-50">
                  <option value="">Pick a theme…</option>
                  {cats.filter((c) => c.enabled).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} · {c.approved_count ?? 0} ready</option>
                  ))}
                </select>
                <input data-testid={`admin-sched-start-${d}`} type="time" value={row.start_time} disabled={!row.enabled}
                  onChange={(e) => setDay(d, { start_time: e.target.value })}
                  className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 font-mono text-sm text-cream outline-none focus:border-magenta disabled:opacity-50" />
                <span className="font-mono text-xs text-mut">→</span>
                <input data-testid={`admin-sched-end-${d}`} type="time" value={row.end_time} disabled={!row.enabled}
                  onChange={(e) => setDay(d, { end_time: e.target.value })}
                  className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 font-mono text-sm text-cream outline-none focus:border-magenta disabled:opacity-50" />
              </div>
            </div>
          );
        })}
      </div>
      {note && <p className="mt-3 text-sm font-bold text-mint" data-testid="admin-schedule-note">{note}</p>}
      {err && <p className="mt-3 text-sm font-bold text-coral" data-testid="admin-schedule-error">{err}</p>}
      <button data-testid="admin-schedule-save" onClick={save} disabled={!!busy}
        className="neon-btn mt-5 w-full rounded-full py-3.5 font-display font-extrabold uppercase tracking-wide disabled:opacity-60">
        {busy ? "Saving…" : "Save weekly schedule"}
      </button>
    </>
  );
}

function NightlyArchivesPanel({ authHeader }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const load = () => {
    fetch(`${API_URL}/admin/nightly-archives`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function runNow() {
    setBusy(true); setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/nightly-reset/run`, { method: "POST", headers: authHeader() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "failed");
      setNote(d.skipped ? `Already reset for ${d.date || "today"}` : `Archived ${d.venues} venue${d.venues === 1 ? "" : "s"} · ${d.rows} score rows flagged`);
      load();
    } catch (ex) {
      setNote("Reset failed — try again");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div data-testid="admin-nightly-panel">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button data-testid="admin-nightly-run" onClick={runNow} disabled={!!busy}
          className="rounded-full bg-magenta/20 px-4 py-1.5 font-mono text-[10px] font-bold uppercase text-[#ff9df2] transition-colors hover:bg-magenta/30 disabled:opacity-60">
          {busy ? "Running…" : "Run the reset now"}
        </button>
        {note && <span className="text-[11px] font-bold text-mint" data-testid="admin-nightly-note">{note}</span>}
      </div>
      {!data ? (
        <p className="py-3 text-sm text-mut">Loading nightly champions…</p>
      ) : !data.archives.length ? (
        <p className="py-3 text-sm text-mut" data-testid="admin-nightly-empty">No archived nights yet — the nightly reset writes the first one automatically.</p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-ice/15">
          <table className="w-full text-left text-sm">
            <thead className="bg-navy-900/80 font-mono text-[10px] uppercase tracking-[0.2em] text-mut">
              <tr><th className="px-4 py-3">Night</th><th className="px-4 py-3">Venue</th><th className="px-4 py-3">Champion</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Games</th></tr>
            </thead>
            <tbody>
              {data.archives.map((a, i) => (
                <tr key={`${a.venue_code}-${a.date}-${i}`} className="border-t border-ice/10" data-testid="admin-nightly-row">
                  <td className="px-4 py-3 font-mono text-[11px] text-mut">{a.date}</td>
                  <td className="px-4 py-3 font-bold text-cream">{a.venue_name}</td>
                  <td className="px-4 py-3 font-bold text-amber-soft">{(a.champion || {}).name || "—"}</td>
                  <td className="px-4 py-3 font-mono-num font-bold text-mint">{(a.champion || {}).total ?? "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-mut">{a.games}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DemoTab({ authHeader }) {
  const [status, setStatus] = useState(null);
  const [intermission, setIntermission] = useState(20);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    fetch(`${API_URL}/admin/demo`, { headers: authHeader() })
      .then(async (r) => {
        if (r.ok) {
          const d = await r.json();
          setStatus(d);
          setIntermission(Math.round((d.intermission_seconds || 1200) / 60));
        }
      })
      .catch(() => {});
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  async function act(kind, body) {
    setBusy(kind);
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/demo/${kind}`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(body || {}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "failed");
      if (kind === "toggle") setNote(d.platform.demo.enabled ? "Sales demo is ON — the demo bar is live" : "Sales demo is OFF — the demo bar went dark");
      if (kind === "reset-game") setNote("Demo restarted — a fresh 4-question game is running");
      if (kind === "reset-scores") setNote("Sample scores wiped — bots restart from zero");
      if (kind === "restore-content") setNote("Original sample specials, photos, events and ads restored");
      load();
    } catch (ex) {
      setNote("That action failed — try again");
    } finally {
      setBusy(null);
    }
  }

  async function saveIntermission() {
    setBusy("intermission");
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/demo/intermission`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ seconds: intermission * 60 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setNote(`Demo intermission is now ${Math.round(d.intermission_seconds / 60)} minutes — applies from the next demo game`);
    } catch (ex) {
      setNote("Could not save — try again");
    } finally {
      setBusy(null);
    }
  }

  if (!status) return <p className="py-4 text-sm text-mut">Loading the demo…</p>;
  return (
    <>
      <ToggleRow testId="admin-demo-toggle" label="Sales Demonstration Mode" hint="A fully populated demo bar with sample players, scores, food photos and a fast 4-question game"
        on={!!status.enabled} onToggle={() => act("toggle", { enabled: !status.enabled })} />
      {note && <p className="mt-4 text-sm font-bold text-mint" data-testid="admin-demo-note">{note}</p>}

      <div className="mt-5 rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4" data-testid="admin-demo-status">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="font-display text-lg font-extrabold text-cream">{status.code}</span>
            <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.2em] text-mut">join code · BrainTap Demo Bar</span>
          </div>
          {status.game ? (
            <span className="rounded-full bg-mint/15 px-4 py-1 font-mono text-[10px] font-bold uppercase text-mint" data-testid="admin-demo-live">
              Live · Q{status.game.q_index}/{status.game.question_total} · {status.game.players} playing
            </span>
          ) : (
            <span className="rounded-full bg-amber/15 px-4 py-1 font-mono text-[10px] font-bold uppercase text-amber-soft">Starting…</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a data-testid="admin-demo-open-tv" href={`/tv/${status.code}`} target="_blank" rel="noreferrer" className="rounded-full bg-magenta/20 px-4 py-2 font-mono text-[10px] font-bold uppercase text-[#ff9df2] transition-colors hover:bg-magenta/30">Open demo big screen</a>
          <a data-testid="admin-demo-preview-player" href={`/play/${status.code}`} target="_blank" rel="noreferrer" className="rounded-full bg-volt/15 px-4 py-2 font-mono text-[10px] font-bold uppercase text-volt transition-colors hover:bg-volt/25">Preview as a player</a>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4">
          <AdminSlider label="Demo intermission" value={intermission} min={2} max={60} step={1} unit="min" onChange={setIntermission} testId="admin-demo-intermission" />
          <button data-testid="admin-demo-intermission-save" onClick={saveIntermission} disabled={!!busy} className="mt-3 w-full rounded-full bg-amber py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-navy-900">Save demo intermission</button>
        </div>
        <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4">
          <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-amber">Demo bar login (for your sales pitch)</div>
          <p className="font-mono text-sm text-cream" data-testid="admin-demo-credentials">{status.email} <span className="text-mut">/</span> BrainTapDemo2026!</p>
          <p className="mt-2 text-[11px] leading-snug text-mut">Sign in at /venue to show owners the dashboard. This account sees only the demo bar.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <button data-testid="admin-demo-reset-game" onClick={() => act("reset-game")} disabled={!!busy} className="rounded-full border-2 border-ice/25 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber disabled:opacity-60">{busy === "reset-game" ? "Restarting…" : "Restart 4-question demo"}</button>
        <button data-testid="admin-demo-reset-scores" onClick={() => act("reset-scores")} disabled={!!busy} className="rounded-full border-2 border-ice/25 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber disabled:opacity-60">{busy === "reset-scores" ? "Wiping…" : "Reset sample scores"}</button>
        <button data-testid="admin-demo-restore-content" onClick={() => act("restore-content")} disabled={!!busy} className="rounded-full border-2 border-ice/25 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-cream transition-all hover:border-amber hover:text-amber disabled:opacity-60">{busy === "restore-content" ? "Restoring…" : "Restore sample content"}</button>
      </div>
    </>
  );
}

function BadgesTab({ authHeader }) {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [custom, setCustom] = useState({ name: "", desc: "", rarity: "bronze" });

  const load = () => {
    fetch(`${API_URL}/admin/badges`, { headers: authHeader() })
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(path, body, okMsg) {
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/badges/${path}`, {
        method: path.includes("award") || path.includes("remove") || path.includes("create") ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "failed");
      setNote(okMsg);
      load();
    } catch (ex) {
      setNote(ex.message === "failed" ? "That action failed — try again" : ex.message);
    }
  }

  if (!data) return <p className="py-4 text-sm text-mut">Loading the badge wall…</p>;
  const groups = {};
  data.badges.forEach((b) => { (groups[b.group] = groups[b.group] || []).push(b); });
  return (
    <>
      <p className="mb-1 text-sm text-mut">48 collectible badges. Legendary ones have painted artwork; the rest render as neon medals. Toggles apply instantly.</p>
      <p className="mb-4 text-sm text-mut">{data.players_with_badges} players hold at least one badge.</p>
      {note && <p className="mb-4 text-sm font-bold text-mint" data-testid="admin-badges-note">{note}</p>}

      <div className="mb-6 rounded-2xl border border-magenta/40 bg-navy-900/60 px-5 py-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-bold text-cream">Award or remove from a player</span>
          <input data-testid="admin-badge-player-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Player name or email"
            className="w-52 rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none focus:border-magenta" />
        </div>
        <p className="text-[11px] text-mut">Use a badge's buttons below to award (even admin-only ones) or strip it. Type the player's name first.</p>
      </div>

      <div className="mb-6 rounded-2xl border border-grape/40 bg-navy-900/60 px-5 py-4">
        <div className="mb-3 text-sm font-bold text-cream">Create a custom badge</div>
        <div className="grid gap-2 sm:grid-cols-3">
          <input data-testid="admin-custom-badge-name" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Badge name" className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none focus:border-magenta" />
          <input data-testid="admin-custom-badge-desc" value={custom.desc} onChange={(e) => setCustom({ ...custom, desc: e.target.value })} placeholder="What it's for" className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none focus:border-magenta" />
          <select data-testid="admin-custom-badge-rarity" value={custom.rarity} onChange={(e) => setCustom({ ...custom, rarity: e.target.value })} className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2 text-sm text-cream outline-none">
            {["bronze", "silver", "gold", "legendary"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <button data-testid="admin-custom-badge-create" onClick={() => act("create", custom, "Custom badge created — find it under Custom")}
          className="mt-3 w-full rounded-full bg-magenta/20 px-4 py-2 font-mono text-[10px] font-bold uppercase text-[#ff9df2] transition-colors hover:bg-magenta/30">+ Create badge</button>
      </div>

      <div className="space-y-6" data-testid="admin-badges-list">
        {Object.entries(groups).map(([group, badges]) => (
          <div key={group}>
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">{group}</div>
            <div className="grid gap-2">
              {badges.map((b) => (
                <div key={b.id} className={`rounded-2xl border px-4 py-3 ${b.enabled ? "border-ice/10 bg-navy-900/60" : "border-coral/30 bg-navy-900/40 opacity-70"}`} data-testid={`admin-badge-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <BadgeArt id={b.id} size={40} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-display text-sm font-extrabold text-cream">{b.name}</span>
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[8px] font-bold uppercase ${RARITY_LABEL[b.rarity] || "text-mut"}`} style={{ background: "rgba(255,255,255,0.06)" }}>{b.rarity}</span>
                      </div>
                      <div className="truncate text-[11px] text-mut">{b.desc}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {b.req_value != null && (
                        <input type="number" defaultValue={b.req_value} min={1}
                          onBlur={(e) => { const v = Number(e.target.value); if (v && v !== b.req_value) act(b.id, { requirement: v }, `${b.name} requirement set to ${v}`); }}
                          className="w-16 rounded-lg border border-ice/20 bg-navy-900 px-2 py-1.5 text-center font-mono text-xs text-cream outline-none" data-testid={`admin-badge-req-${b.id}`} />
                      )}
                      {query.trim() && (
                        <>
                          <button onClick={() => act("award", { badge_id: b.id, player_query: query }, `Awarded ${b.name} to ${query}`)}
                            className="rounded-full bg-mint/15 px-3 py-1.5 font-mono text-[9px] font-bold uppercase text-mint transition-colors hover:bg-mint/25" data-testid={`admin-badge-award-${b.id}`}>Award</button>
                          <button onClick={() => act("remove", { badge_id: b.id, player_query: query }, `Removed ${b.name} from ${query}`)}
                            className="rounded-full bg-coral/10 px-3 py-1.5 font-mono text-[9px] font-bold uppercase text-coral transition-colors hover:bg-coral/20">Strip</button>
                        </>
                      )}
                      <button onClick={() => act(b.id, { enabled: !b.enabled }, `${b.name} ${b.enabled ? "disabled" : "enabled"}`)}
                        className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${b.enabled ? "bg-mint" : "bg-navy-700"}`} data-testid={`admin-badge-toggle-${b.id}`}>
                        <span className={`h-4 w-4 rounded-full bg-white transition-transform ${b.enabled ? "translate-x-4" : ""}`} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const RARITY_LABEL = { bronze: "text-[#e8b98a]", silver: "text-[#c9d3e4]", gold: "text-amber", legendary: "text-[#ff9df2]" };

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("tk_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [cfg, setCfg] = useState(null);
  const [plat, setPlat] = useState(null);
  const [busy, setBusy] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("show");
  const [cats, setCats] = useState([]);
  const [bank, setBank] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [bankFilter, setBankFilter] = useState({ search: "", category: "", subcategory: "", difficulty: "", status: "", page: 1 });
  const [genForm, setGenForm] = useState({ category_id: "", subcategory: "", difficulty: "medium", count: 12 });
  const [openCat, setOpenCat] = useState("");

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  async function loadTaxonomy() {
    try {
      const r = await fetch(`${API_URL}/admin/categories`, { headers: authHeader() });
      if (r.ok) setCats((await r.json()).categories);
    } catch (e) { /* optional */ }
  }

  async function loadBank(f) {
    try {
      const p = new URLSearchParams({ search: f.search, category: f.category, subcategory: f.subcategory, difficulty: f.difficulty, status: f.status, page: f.page });
      const r = await fetch(`${API_URL}/admin/bank?${p.toString()}`, { headers: authHeader() });
      if (r.ok) setBank(await r.json());
    } catch (e) { /* optional */ }
  }

  const refreshOverview = (t) => {
    fetch(`${API_URL}/admin/overview`, { headers: { Authorization: `Bearer ${t}` } })
      .then(async (r) => { if (r.ok) setOverview(await r.json()); })
      .catch(() => { /* optional */ });
  };

  useEffect(() => {
    if (!token) return;
    let alive = true;
    fetch(`${API_URL}/game-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { if (!r.ok) throw new Error("unauthorized"); if (alive) setCfg((await r.json()).game_config); })
      .catch(() => { sessionStorage.removeItem("tk_admin_token"); if (alive) setToken(""); });
    fetch(`${API_URL}/admin/platform`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { if (r.ok && alive) setPlat((await r.json()).platform); })
      .catch(() => {});
    refreshOverview(token);
    loadTaxonomy();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (token && tab === "bank") loadBank(bankFilter);
  }, [token, tab, bankFilter]);

  async function login(e) {
    e.preventDefault();
    setNote("");
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) throw new Error("Wrong email or password");
      const { token: t } = await r.json();
      const me = await fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      const md = await me.json();
      if (md.role !== "admin") throw new Error("Admin only — that account is not the website admin");
      sessionStorage.setItem("tk_admin_token", t);
      setToken(t);
    } catch (ex) {
      setNote(ex.message);
    }
  }

  async function save() {
    try {
      const r = await fetch(`${API_URL}/game-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          questions: Number(cfg.questions), question_time: Number(cfg.question_time),
          reveal_time: Number(cfg.reveal_time), break_time: Number(cfg.break_time),
          difficulty: cfg.difficulty, intermission_code: !!cfg.intermission_code,
          kick_absent: !!cfg.kick_absent, takeover_video: !!cfg.takeover_video,
          phone_sounds: !!cfg.phone_sounds, ads_on_tv: !!cfg.ads_on_tv, home_play: !!cfg.home_play,
          categories: cfg.categories || [], subcategories: cfg.subcategories || [],
          difficulties: cfg.difficulties || [],
        }),
      });
      if (!r.ok) throw new Error();
      setCfg((await r.json()).game_config);
      setNote("Saved — applies from the next game");
    } catch (ex) {
      setNote("Could not save — try again");
    }
  }

  async function savePlatform() {
    setBusy("platform");
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/platform`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(plat),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "failed");
      setPlat(data.platform);
      setNote("Platform settings saved — live everywhere within seconds");
    } catch (ex) {
      setNote(ex.message === "failed" ? "Could not save — try again" : ex.message);
    } finally {
      setBusy(null);
    }
  }

  async function generateTrivia() { /* identical to previous gate */ 
    setBusy("generate");
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          count: Number(genForm.count) || 12,
          category_id: genForm.category_id || undefined,
          subcategory: genForm.subcategory || undefined,
          grade: { easy: "6th", medium: "8th", hard: "11th", expert: "college" }[genForm.difficulty] || "8th",
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "failed");
      setNote(`Added ${data.added} draft questions (${data.difficulty}) — review them in the list below`);
      loadTaxonomy();
      loadBank(bankFilter);
      refreshOverview(token);
    } catch (ex) {
      setNote("Generation failed — check the AI key budget and try again");
    } finally {
      setBusy(null);
    }
  }

  async function updateCategory(cid, body) {
    await fetch(`${API_URL}/admin/categories/${cid}`, {
      method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    loadTaxonomy();
  }

  async function addCategory() {
    const name = window.prompt("New category name");
    if (!name || name.trim().length < 3) return;
    const r = await fetch(`${API_URL}/admin/categories`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (!r.ok) {
      setNote(await r.json().then((d) => d.detail || "Could not add that category"));
      return;
    }
    setNote(`Category "${name.trim()}" added`);
    loadTaxonomy();
  }

  async function reorderCategory(cid, dir) {
    const ids = cats.map((c) => c.id);
    const i = ids.indexOf(cid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await fetch(`${API_URL}/admin/categories/reorder`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ ids }),
    });
    loadTaxonomy();
  }

  async function addSubcategory(cid) {
    const name = window.prompt("New subcategory name");
    if (!name || name.trim().length < 2) return;
    await updateCategory(cid, { add_subcategory: name.trim() });
  }

  async function setQuestionStatus(id, status) {
    await fetch(`${API_URL}/admin/bank/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ status }),
    });
    loadBank(bankFilter);
    loadTaxonomy();
  }

  async function deleteQuestion(id) {
    if (!window.confirm("Delete this question forever?")) return;
    await fetch(`${API_URL}/admin/bank/${id}`, { method: "DELETE", headers: authHeader() });
    loadBank(bankFilter);
    loadTaxonomy();
  }

  async function resetStandings() {
    if (!window.confirm("Wipe ALL scores, games and player stats across every venue? This cannot be undone.")) return;
    setBusy("reset");
    setNote("");
    try {
      const r = await fetch(`${API_URL}/admin/reset-standings`, { method: "POST", headers: authHeader() });
      if (!r.ok) throw new Error();
      setNote("Standings wiped — the whole platform is back to zero");
      refreshOverview(token);
    } catch (ex) {
      setNote("Reset failed — try again");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-navy-900 px-5 py-8 text-cream" data-testid="admin-page">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoLockup className="h-10 w-auto" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-amber">Admin dashboard</h1>
          </div>
          <Link data-testid="admin-back-home" to="/" className="flex items-center gap-2 rounded-full border-2 border-ice/25 px-5 py-2.5 text-sm font-bold transition-all hover:border-amber hover:text-amber">
            <ArrowLeft size={16} /> Site
          </Link>
        </div>

        {!token ? (
          <form onSubmit={login} className="mx-auto max-w-md space-y-4 rounded-3xl border-2 border-grape/50 bg-navy-800 p-8" data-testid="admin-login-card">
            <p className="text-sm text-mut">Website admin only. Sign in with your admin account to shape every game on every screen.</p>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-cream outline-none focus:border-amber" />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" className="w-full rounded-2xl border-2 border-ice/20 bg-navy-900/70 px-5 py-3 text-cream outline-none focus:border-amber" />
            {note && <p className="text-sm font-bold text-coral" data-testid="admin-error">{note}</p>}
            <button type="submit" data-testid="admin-login-submit" className="w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5">Sign in</button>
          </form>
        ) : !cfg || !plat ? (
          <p className="flex items-center gap-2 text-sm text-mut"><Loader2 className="animate-spin" size={16} /> Loading controls…</p>
        ) : (
          <>
            <div className="mb-5 flex flex-wrap gap-2" data-testid="admin-tabs">
              {[["show", "Show"], ["schedule", "Weekly Schedule"], ["assembly", "Game Assembly"], ["bank", "Question Bank"], ["badges", "Badges"], ["demo", "Sales Demo"], ["platform", "Platform"]].map(([k, label]) => (
                <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
                  className={`rounded-full px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] transition-all ${tab === k ? "bg-magenta text-white shadow-[0_0_14px_rgba(255,43,253,0.4)]" : "border border-ice/20 text-mut hover:text-cream"}`}>
                  {label}
                </button>
              ))}
            </div>
            {note && <p className="mb-4 text-sm font-bold text-mint" data-testid="admin-saved-note">{note}</p>}

            <div className="rounded-3xl border border-ice/10 bg-navy-800/60 p-6 lg:p-8">
              {tab === "show" && (
                <>
                  <p className="mb-5 text-sm text-mut">Shape every game. Changes apply from the next game onward.</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <AdminSlider label="Questions per game" value={cfg.questions} min={5} max={100} step={5} unit="questions" onChange={(v) => setCfg({ ...cfg, questions: v })} testId="admin-questions" />
                    <AdminSlider label="Answer time" value={cfg.question_time} min={5} max={30} step={1} unit="seconds" onChange={(v) => setCfg({ ...cfg, question_time: v })} testId="admin-question-time" />
                    <AdminSlider label="Answer reveal" value={cfg.reveal_time} min={3} max={15} step={1} unit="seconds" onChange={(v) => setCfg({ ...cfg, reveal_time: v })} testId="admin-reveal-time" />
                    <AdminSlider label="Intermission length" value={Math.round(cfg.break_time / 60)} min={2} max={20} step={1} unit="minutes" onChange={(v) => setCfg({ ...cfg, break_time: v * 60 })} testId="admin-break-time" />
                    <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4 sm:col-span-2" data-testid="admin-difficulty">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-cream">Writing level for fresh AI questions</span>
                        <span className="font-mono text-sm font-bold text-amber-soft" data-testid="admin-difficulty-value">{cfg.difficulty === "college" ? "College" : `${cfg.difficulty} grade`}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-5 gap-1.5">
                        {DIFFICULTIES.map((d) => (
                          <button key={d} type="button" data-testid={`admin-difficulty-${d}`} onClick={() => setCfg({ ...cfg, difficulty: d })}
                            className={`rounded-xl px-1 py-2 font-mono text-[10px] font-bold uppercase tracking-wide transition-all ${cfg.difficulty === d ? "bg-amber text-navy-900" : "bg-navy-800/80 text-mut ring-1 ring-ice/15 hover:text-cream"}`}>
                            {d === "college" ? "College" : d}
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] leading-snug text-mut">Fresh questions are written at this reading level — new games pick them up automatically.</p>
                    </div>
                    {TOGGLE_META.map(([key, label]) => (
                      <ToggleRow key={key} testId={`admin-toggle-${key.replace(/_/g, "-")}`} label={label} on={!!cfg[key]} onToggle={() => setCfg({ ...cfg, [key]: !cfg[key] })} />
                    ))}
                  </div>
                  {overview && (
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-mut" data-testid="admin-overview">
                      Bank: {overview.bank_total} questions · {overview.players} players · {overview.active_games} live games
                    </p>
                  )}
                  <button data-testid="admin-game-config-save" onClick={save} disabled={!!busy} className="neon-btn mt-6 w-full rounded-full py-3.5 font-display font-extrabold uppercase tracking-wide disabled:opacity-60">Save show controls</button>
                  <button data-testid="admin-reset-standings" onClick={resetStandings} disabled={!!busy} className="mt-3 w-full rounded-full border-2 border-coral/60 py-3 font-display font-extrabold uppercase tracking-wide text-coral transition-all hover:bg-coral/10 disabled:opacity-60">
                    {busy === "reset" ? "Wiping…" : "Reset all standings to zero"}
                  </button>
                </>
              )}

              {tab === "assembly" && (
                <>
                  <p className="mb-1 text-sm text-mut">Pick what tonight's games draw from — saved to your show setup.</p>
                  <p className="mb-4 text-[11px] text-mut">One category = category-specific games. Several = mixed games. Nothing selected = mixed league night across every enabled category.</p>
                  <div className="rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4" data-testid="admin-assembly-cats">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-cream">Categories</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut">{(cfg.categories || []).length || "all enabled"} selected</span>
                    </div>
                    <div className="grid max-h-60 grid-cols-2 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-3">
                      {cats.filter((c) => c.enabled).map((c) => {
                        const on = (cfg.categories || []).includes(c.id);
                        return (
                          <button key={c.id} type="button" data-testid={`asm-cat-${c.id}`}
                            onClick={() => setCfg({ ...cfg, categories: on ? (cfg.categories || []).filter((x) => x !== c.id) : [...(cfg.categories || []), c.id] })}
                            className={`rounded-xl px-2.5 py-2 text-left leading-tight transition-all ${on ? "bg-magenta/20 text-cream ring-2 ring-magenta" : "bg-navy-800/70 text-mut ring-1 ring-ice/10 hover:text-cream"}`}>
                            <span className="block truncate text-[11px] font-bold">{c.name}</span>
                            <span className={`block font-mono text-[9px] ${(c.approved_count ?? 0) ? "text-mut" : "text-coral"}`}>{c.approved_count ?? 0} ready{c.draft_count ? ` · ${c.draft_count} drafts` : ""}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-ice/15 bg-navy-900/60 px-5 py-4" data-testid="admin-assembly-difficulty">
                    <div className="mb-3 text-sm font-bold text-cream">Question difficulty</div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {["easy", "medium", "hard", "expert"].map((d) => {
                        const on = (cfg.difficulties || []).includes(d);
                        return (
                          <button key={d} type="button" data-testid={`asm-difficulty-${d}`}
                            onClick={() => setCfg({ ...cfg, difficulties: on ? (cfg.difficulties || []).filter((x) => x !== d) : [...(cfg.difficulties || []), d] })}
                            className={`rounded-xl py-2 font-mono text-[10px] font-bold uppercase tracking-wide transition-all ${on ? "bg-volt text-navy-900 shadow-[0_0_12px_rgba(0,229,255,0.4)]" : "bg-navy-800/80 text-mut ring-1 ring-ice/15 hover:text-cream"}`}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-[11px] text-mut">Optional — nothing selected plays every difficulty.</p>
                  </div>
                  <button data-testid="admin-assembly-save" onClick={save} disabled={!!busy} className="neon-btn mt-6 w-full rounded-full py-3.5 font-display font-extrabold uppercase tracking-wide disabled:opacity-60">Save game assembly</button>
                </>
              )}

              {tab === "bank" && (
                <>
                  <div className="rounded-2xl border border-grape/40 bg-navy-900/60 px-5 py-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-cream">Write new questions</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut">saved as drafts</span>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <select data-testid="admin-bank-gen-category" value={genForm.category_id} onChange={(e) => setGenForm({ ...genForm, category_id: e.target.value, subcategory: "" })}
                        className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2.5 text-sm text-cream outline-none focus:border-magenta">
                        <option value="">Any enabled category</option>
                        {cats.filter((c) => c.enabled).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <select data-testid="admin-bank-gen-subcategory" value={genForm.subcategory} onChange={(e) => setGenForm({ ...genForm, subcategory: e.target.value })}
                        className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2.5 text-sm text-cream outline-none focus:border-magenta">
                        <option value="">Any subcategory</option>
                        {((cats.find((c) => c.id === genForm.category_id) || {}).subcategories || []).map((s) => (
                          <option key={s.name} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                      <select data-testid="admin-bank-gen-difficulty" value={genForm.difficulty} onChange={(e) => setGenForm({ ...genForm, difficulty: e.target.value })}
                        className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2.5 text-sm text-cream outline-none focus:border-magenta">
                        {["easy", "medium", "hard", "expert"].map((d) => <option key={d} value={d}>Difficulty: {d}</option>)}
                      </select>
                      <input data-testid="admin-bank-gen-count" type="number" min="1" max="50" value={genForm.count}
                        onChange={(e) => setGenForm({ ...genForm, count: e.target.value })}
                        className="rounded-xl border border-ice/20 bg-navy-900/70 px-3 py-2.5 text-sm text-cream outline-none focus:border-magenta" />
                    </div>
                    <button data-testid="admin-bank-generate" onClick={generateTrivia} disabled={!!busy}
                      className="neon-btn mt-3 w-full rounded-full py-3 font-display text-sm font-extrabold uppercase tracking-wide disabled:opacity-60">
                      {busy === "generate" ? "Writing…" : `Write ${genForm.count || 12} questions`}
                    </button>
                  </div>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    <input data-testid="admin-bank-search" placeholder="Search the bank…" value={bankFilter.search}
                      onChange={(e) => setBankFilter({ ...bankFilter, search: e.target.value, page: 1 })}
                      className="rounded-xl border border-ice/20 bg-navy-900/70 px-4 py-2.5 text-sm text-cream outline-none focus:border-magenta sm:col-span-2" />
                    <select data-testid="admin-bank-filter-status" value={bankFilter.status} onChange={(e) => setBankFilter({ ...bankFilter, status: e.target.value, page: 1 })}
                      className="rounded-xl border border-ice/20 bg-navy-900/70 px-4 py-2.5 text-sm text-cream outline-none">
                      <option value="">All statuses</option>
                      {["draft", "reviewed", "approved", "retired"].map((s) => <option key={s} value={s}>Status: {s}</option>)}
                    </select>
                    <select data-testid="admin-bank-filter-difficulty" value={bankFilter.difficulty} onChange={(e) => setBankFilter({ ...bankFilter, difficulty: e.target.value, page: 1 })}
                      className="rounded-xl border border-ice/20 bg-navy-900/70 px-4 py-2.5 text-sm text-cream outline-none">
                      <option value="">All difficulties</option>
                      {["easy", "medium", "hard", "expert"].map((d) => <option key={d} value={d}>Difficulty: {d}</option>)}
                    </select>
                  </div>

                  <div className="mt-3 space-y-2" data-testid="admin-bank-rows">
                    {bank.items.map((item) => (
                      <div key={item.id} className="rounded-2xl border border-ice/10 bg-navy-900/60 px-4 py-3" data-testid="admin-bank-row">
                        <p className="text-sm font-bold leading-snug text-cream">{item.q}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-mut">
                          {item.main_category} · {item.subcategory} · {item.difficulty} · <span className={item.status === "approved" ? "text-mint" : item.status === "retired" ? "text-coral" : "text-amber-soft"}>{item.status}</span> · used {item.used_count}×
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {item.status !== "approved" && (
                            <button data-testid={`admin-bank-approve-${item.id}`} onClick={() => setQuestionStatus(item.id, "approved")}
                              className="rounded-full bg-mint/20 px-3 py-1 font-mono text-[10px] font-bold uppercase text-mint transition-colors hover:bg-mint/30">Approve</button>
                          )}
                          {item.status === "draft" && (
                            <button onClick={() => setQuestionStatus(item.id, "reviewed")}
                              className="rounded-full bg-volt/15 px-3 py-1 font-mono text-[10px] font-bold uppercase text-volt">Mark reviewed</button>
                          )}
                          {item.status !== "retired" && (
                            <button data-testid={`admin-bank-retire-${item.id}`} onClick={() => setQuestionStatus(item.id, "retired")}
                              className="rounded-full bg-coral/15 px-3 py-1 font-mono text-[10px] font-bold uppercase text-coral transition-colors hover:bg-coral/25">Retire</button>
                          )}
                          <button data-testid={`admin-bank-delete-${item.id}`} onClick={() => deleteQuestion(item.id)}
                            className="rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase text-mut transition-colors hover:text-coral">Delete</button>
                        </div>
                      </div>
                    ))}
                    {!bank.items.length && <p className="py-6 text-center text-sm text-mut">No questions match those filters.</p>}
                  </div>
                  <div className="mt-3 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-mut">
                    <button data-testid="admin-bank-prev" disabled={bank.page <= 1} onClick={() => setBankFilter({ ...bankFilter, page: bank.page - 1 })}
                      className="rounded-full border border-ice/20 px-4 py-1.5 disabled:opacity-40">Prev</button>
                    <span>{bank.page} / {bank.pages} · {bank.total} questions</span>
                    <button data-testid="admin-bank-next" disabled={bank.page >= bank.pages} onClick={() => setBankFilter({ ...bankFilter, page: bank.page + 1 })}
                      className="rounded-full border border-ice/20 px-4 py-1.5 disabled:opacity-40">Next</button>
                  </div>

                  <div className="mt-8 border-t border-ice/10 pt-5">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-bold text-cream">Manage categories</span>
                      <button data-testid="admin-cat-add" onClick={addCategory} className="rounded-full bg-magenta/20 px-4 py-1.5 font-mono text-[10px] font-bold uppercase text-[#ff9df2] transition-colors hover:bg-magenta/30">+ Add category</button>
                    </div>
                    <div className="space-y-1.5">
                      {cats.map((c, i) => (
                        <div key={c.id} className="rounded-xl border border-ice/10 bg-navy-900/60">
                          <div className="flex items-center gap-2 px-3 py-2">
                            <button onClick={() => setOpenCat(openCat === c.id ? "" : c.id)} className="flex-1 truncate text-left text-sm font-bold text-cream" data-testid={`admin-cat-row-${c.id}`}>
                              {i + 1}. {c.name} <span className="ml-1 font-mono text-[10px] font-normal text-mut">{c.question_count} q</span>
                            </button>
                            <button title="Move up" onClick={() => reorderCategory(c.id, -1)} className="px-1 font-mono text-xs text-mut transition-colors hover:text-amber">↑</button>
                            <button title="Move down" onClick={() => reorderCategory(c.id, 1)} className="px-1 font-mono text-xs text-mut transition-colors hover:text-amber">↓</button>
                            <button title="Rename" onClick={() => { const n = window.prompt("Rename category", c.name); if (n && n.trim().length >= 3) updateCategory(c.id, { name: n.trim() }); }}
                              className="px-1 font-mono text-[10px] text-mut transition-colors hover:text-amber">✎</button>
                            <button onClick={() => updateCategory(c.id, { enabled: !c.enabled })} title={c.enabled ? "Disable" : "Enable"}
                              className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${c.enabled ? "bg-mint" : "bg-navy-700"}`} data-testid={`admin-cat-toggle-${c.id}`}>
                              <span className={`h-4 w-4 rounded-full bg-white transition-transform ${c.enabled ? "translate-x-4" : ""}`} />
                            </button>
                          </div>
                          {openCat === c.id && (
                            <div className="border-t border-ice/10 px-3 py-2.5">
                              <div className="flex flex-wrap gap-1.5">
                                {(c.subcategories || []).map((s) => (
                                  <button key={s.name} onClick={() => updateCategory(c.id, { subcategories: c.subcategories.map((x) => (x.name === s.name ? { ...x, enabled: !x.enabled } : x)) })}
                                    className={`rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase ${s.enabled ? "bg-mint/15 text-mint" : "bg-navy-800 text-mut/60 line-through"}`}>
                                    {s.name}
                                  </button>
                                ))}
                              </div>
                              <button onClick={() => addSubcategory(c.id)} className="mt-2 font-mono text-[10px] uppercase tracking-wide text-mut transition-colors hover:text-amber" data-testid={`admin-cat-addsub-${c.id}`}>+ Add subcategory</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {tab === "schedule" && <ScheduleTab cats={cats} authHeader={authHeader} />}

              {tab === "platform" && <PlatformTab plat={plat} setPlat={setPlat} save={savePlatform} busy={busy} authHeader={authHeader} />}

              {tab === "demo" && <DemoTab authHeader={authHeader} />}

              {tab === "badges" && <BadgesTab authHeader={authHeader} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
